import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { getDailyCloseProductHeads, getDailyCloseProductItems } from './apis'
import type { DailyCloseProductHead, DailyCloseProductItem, DailyCloseProductItemSearch, DailyCloseProductSearch } from './types'

const PATH = ['RP12', 'INVENTORY', 'DAILY_CLOSE_PRODUCT'] as const

export const dailyCloseProductQueryKeys = {
  all: PATH,
  headsAll: [...PATH, 'HEADS'] as const,
  heads: (params: DailyCloseProductSearch | null) => [...PATH, 'HEADS', params] as const,
  items: (params: DailyCloseProductItemSearch | null) => [...PATH, 'ITEMS', params] as const,
}

/**
 * 헤더 조회. 다른 탭에 갔다가 돌아와 URL 로 복원한 같은 조건은 캐시를 그대로 보여 주고 다시 받지 않는다(LOGISPRD-17334).
 *
 * 탭을 옮기면 화면이 언마운트되고 돌아올 때 조회 번호가 1부터 다시 시작한다. 예전에는 키에 조회 번호가 들어가
 * staleTime 0 과 겹쳐 복귀할 때마다 재조회가 나갔다. 이제 키는 조건만 쓰고 스스로 다시 받지 않는다.
 * [조회] 는 늘 새로 받는다 — 화면이 같은 조건은 refetch, 새 조건은 이전 헤더 캐시를 지운 뒤 키를 바꾼다.
 * 캐시는 탭을 닫으면 MenuOnSessionWrap.remove 가 RP12 접두사로 지운다.
 */
export const useGetDailyCloseProductHeads = (params: DailyCloseProductSearch | null): UseQueryResult<DailyCloseProductHead[], Error> =>
  useQuery({
    meta: { errorToast: true },
    queryKey: dailyCloseProductQueryKeys.heads(params),
    queryFn: async () => {
      if (!params) throw new Error('일마감 헤더 조회 조건이 없습니다.')
      return (await getDailyCloseProductHeads(params)).data.items
    },
    enabled: params != null,
    staleTime: Infinity,
    gcTime: Infinity,
  })

export const useGetDailyCloseProductItems = (params: DailyCloseProductItemSearch | null): UseQueryResult<ApiPageResponse<DailyCloseProductItem>, Error> =>
  useQuery({
    meta: { errorToast: true },
    queryKey: dailyCloseProductQueryKeys.items(params),
    queryFn: async () => {
      if (!params) throw new Error('일마감 상세 조회 조건이 없습니다.')
      return (await getDailyCloseProductItems(params)).data
    },
    enabled: params != null,
  })
